import { useState } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import QuotePopup from '@/pages/home/components/QuotePopup';


const stats = [
  { value: '10+', label: 'Years in Business', icon: 'ri-calendar-check-line' },
  { value: '50+', label: 'Countries Served', icon: 'ri-global-line' },
  { value: '200+', label: 'Mill Partners', icon: 'ri-building-4-line' },
  { value: '500K+', label: 'MT Shipped Annually', icon: 'ri-ship-line' },
];

const milestones = [
  { year: '2013', title: 'Founded in Guangzhou', desc: 'Steelinkglobal was established with a mission to connect global buyers with verified Chinese steel mills.' },
  { year: '2015', title: 'First Major Export', desc: 'Completed our first 5,000 MT export order to Southeast Asia, establishing our logistics network.' },
  { year: '2017', title: 'ISO 9001 Certified', desc: 'Achieved ISO 9001:2015 certification, formalizing our quality management processes.' },
  { year: '2019', title: 'Expanded to 30 Countries', desc: 'Grew our customer base to 30+ countries across Africa, Middle East, and Southeast Asia.' },
  { year: '2021', title: 'SGS Partnership', desc: 'Partnered with SGS for third-party inspection services, giving buyers greater confidence.' },
  { year: '2023', title: '10 Years of Excellence', desc: 'Celebrated 10 years with 500,000+ MT shipped and 200+ verified mill partners.' },
];

const team = [
  { name: 'David Chen', role: 'CEO & Founder', image: 'https://readdy.ai/api/search-image?query=professional%20Asian%20male%20business%20executive%20portrait%20clean%20background%20suit%20confident%20smile&width=300&height=300&seq=team-ceo-1&orientation=squarish' },
  { name: 'Sarah Liu', role: 'Head of Sales', image: 'https://readdy.ai/api/search-image?query=professional%20Asian%20female%20business%20executive%20portrait%20clean%20background%20confident%20smile%20corporate&width=300&height=300&seq=team-sales-1&orientation=squarish' },
  { name: 'Michael Wang', role: 'Technical Director', image: 'https://readdy.ai/api/search-image?query=professional%20Asian%20male%20engineer%20technical%20director%20portrait%20clean%20background%20corporate&width=300&height=300&seq=team-tech-1&orientation=squarish' },
  { name: 'Emily Zhang', role: 'Logistics Manager', image: 'https://readdy.ai/api/search-image?query=professional%20Asian%20female%20logistics%20manager%20portrait%20clean%20background%20corporate%20smile&width=300&height=300&seq=team-logistics-1&orientation=squarish' },
];

const certs = [
  { name: 'ISO 9001:2015', desc: 'Quality Management System', icon: 'ri-award-line' },
  { name: 'SGS Verified', desc: 'Third-Party Inspection Partner', icon: 'ri-shield-check-line' },
  { name: 'CE Certified', desc: 'European Conformity Standards', icon: 'ri-verified-badge-line' },
  { name: 'TUV Approved', desc: 'Technical Inspection Association', icon: 'ri-medal-line' },
];

export default function AboutPage() {
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />
      {quoteOpen && <QuotePopup onClose={() => setQuoteOpen(false)} />}

      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-top"
          style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=large%20steel%20manufacturing%20factory%20interior%20industrial%20production%20line%20workers%20professional%20photography%20dramatic%20lighting&width=1400&height=600&seq=about-hero-1&orientation=landscape')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/40" />
        <div className="relative max-w-[1420px] mx-auto px-6">
          <span className="inline-block px-3 py-1 text-white text-xs font-black rounded mb-4 tracking-widest uppercase" style={{ backgroundColor: '#0a2342' }}>Our Story</span>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-6 max-w-2xl leading-tight">
            China&apos;s Trusted Steel Sourcing Partner
          </h1>
          <p className="text-gray-300 text-lg max-w-xl leading-relaxed">
            Since 2013, Steel Link Global has been bridging the gap between global buyers and China&apos;s best steel mills — delivering quality, reliability, and competitive pricing to 50+ countries.
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            <button onClick={() => setQuoteOpen(true)} className="px-6 py-3 text-white font-black rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap" style={{ backgroundColor: '#0a2342' }}>
              Get a Quote <i className="ri-arrow-right-line ml-2" />
            </button>
            <a href="/contact" className="px-6 py-3 border border-white/40 text-white font-semibold rounded-md text-sm hover:bg-white/10 transition-colors whitespace-nowrap">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14" style={{ backgroundColor: '#0a2342' }}>
        <div className="max-w-[1420px] mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full mx-auto mb-3" style={{ backgroundColor: 'rgba(126,184,247,0.15)' }}>
                <i className={`${s.icon} text-xl`} style={{ color: '#7eb8f7' }} />
              </div>
              <div className="text-3xl font-black text-white">{s.value}</div>
              <div className="text-sm font-semibold mt-1" style={{ color: '#7eb8f7' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission + Vision */}
      <section className="py-20">
        <div className="max-w-[1420px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-xs font-black tracking-widest uppercase" style={{ color: '#0a2342' }}>Who We Are</span>
            <h2 className="text-3xl font-black text-gray-900 mt-2 mb-6">More Than a Steel Supplier</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Steel Link Global is a China-based steel trading and sourcing company with deep roots in Guangdong Province — the heart of China&apos;s manufacturing industry. We work directly with verified steel mills across China to source the best products at competitive prices.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Our team of experienced steel professionals handles everything from product sourcing and quality inspection to export documentation and logistics coordination. We speak your language — literally and figuratively.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Whether you&apos;re importing 25 MT or 5,000 MT, we treat every order with the same level of care and professionalism. Our repeat customer rate of over 85% speaks for itself.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="https://readdy.ai/api/search-image?query=steel%20coil%20warehouse%20industrial%20storage%20professional%20photography%20China%20factory&width=400&height=300&seq=about-img-1&orientation=landscape" alt="Steel warehouse" className="rounded-lg w-full h-48 object-cover object-top" />
            <img src="https://readdy.ai/api/search-image?query=steel%20pipe%20bundle%20industrial%20product%20quality%20inspection%20professional%20photography&width=400&height=300&seq=about-img-2&orientation=landscape" alt="Quality inspection" className="rounded-lg w-full h-48 object-cover object-top mt-6" />
            <img src="https://readdy.ai/api/search-image?query=cargo%20ship%20container%20port%20loading%20steel%20export%20professional%20photography&width=400&height=300&seq=about-img-3&orientation=landscape" alt="Export shipping" className="rounded-lg w-full h-48 object-cover object-top -mt-6" />
            <img src="https://readdy.ai/api/search-image?query=business%20team%20meeting%20office%20professional%20Asian%20corporate%20photography&width=400&height=300&seq=about-img-4&orientation=landscape" alt="Our team" className="rounded-lg w-full h-48 object-cover object-top" />
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1420px] mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-black tracking-widest uppercase" style={{ color: '#0a2342' }}>Our Journey</span>
            <h2 className="text-3xl font-black text-gray-900 mt-2">10 Years of Growth</h2>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px hidden lg:block" style={{ backgroundColor: '#c7d9f0' }} />
            <div className="space-y-8">
              {milestones.map((m, i) => (
                <div key={m.year} className={`flex flex-col lg:flex-row items-center gap-6 ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  <div className={`flex-1 ${i % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                    <div className="bg-white rounded-lg p-6 border border-gray-100 inline-block max-w-sm">
                      <span className="font-black text-sm" style={{ color: '#0a2342' }}>{m.year}</span>
                      <h3 className="font-bold text-gray-900 mt-1 mb-2">{m.title}</h3>
                      <p className="text-gray-500 text-sm">{m.desc}</p>
                    </div>
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full border-4 border-white flex-shrink-0 z-10" style={{ backgroundColor: '#0a2342' }}>
                    <i className="ri-checkbox-circle-line text-white" />
                  </div>
                  <div className="flex-1 hidden lg:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20">
        <div className="max-w-[1420px] mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-black tracking-widest uppercase" style={{ color: '#0a2342' }}>The People Behind It</span>
            <h2 className="text-3xl font-black text-gray-900 mt-2">Meet Our Team</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member) => (
              <div key={member.name} className="text-center group">
                <div className="w-full aspect-square rounded-lg overflow-hidden mb-4">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                </div>
                <h3 className="font-bold text-gray-900">{member.name}</h3>
                <p className="text-sm font-semibold" style={{ color: '#1e5fa8' }}>{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-[1420px] mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-xs font-black tracking-widest uppercase" style={{ color: '#7eb8f7' }}>Quality Assurance</span>
            <h2 className="text-3xl font-black text-white mt-2">Certifications &amp; Standards</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {certs.map((c) => (
              <div key={c.name} className="bg-gray-800 rounded-lg p-6 text-center border border-gray-700">
                <div className="w-12 h-12 flex items-center justify-center rounded-full mx-auto mb-4" style={{ backgroundColor: '#0a2342' }}>
                  <i className={`${c.icon} text-xl`} style={{ color: '#7eb8f7' }} />
                </div>
                <h3 className="font-bold text-white mb-1">{c.name}</h3>
                <p className="text-gray-400 text-sm">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" style={{ backgroundColor: '#0a2342' }}>
        <div className="max-w-[1420px] mx-auto px-6 text-center">
          <h2 className="text-3xl font-black text-white mb-3">Ready to Work Together?</h2>
          <p className="mb-8 max-w-xl mx-auto" style={{ color: '#7eb8f7' }}>Join 500+ companies worldwide who trust Steel Link Global for their steel sourcing needs.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button onClick={() => setQuoteOpen(true)} className="px-8 py-3 bg-white font-black rounded-md text-sm hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap" style={{ color: '#0a2342' }}>
              Get a Quote
            </button>
            <a href="/contact" className="px-8 py-3 border-2 border-white/40 text-white font-black rounded-md text-sm hover:bg-white/10 transition-colors whitespace-nowrap">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
